import { type KeyboardEvent, useCallback, useMemo } from "react";
import type { GestureResponderEvent } from "react-native";
import { useLink } from "solito/link";
import { isWeb } from "tamagui";

/** Solito link options forwarded to `useLink` for web routing. */
export type SolitoLinkOptions = {
  /** Replace the current history entry instead of pushing. */
  replace?: boolean;
  /** Scroll to top on navigation (web). @default true */
  scroll?: boolean;
  /** Perform a shallow route change (web — Next.js). */
  shallow?: boolean;
};

export type InteractiveKind = "static" | "button" | "link";

/**
 * Flat interactive contract — `kind` chooses the rendered shape (default
 * `"static"`). Types are intentionally loose: `href` / `replace` / etc. are
 * accepted on every kind but only respected when `kind === "link"`. Strict
 * per-kind types would force an `?: never` discriminated union, which TS
 * collapses through JSX spread and breaks forwarding components like
 * ResourceCard. Callers self-document via `kind`; the hook handles dispatch.
 */
export type InteractiveProps = SolitoLinkOptions & {
  kind?: InteractiveKind;
  onPress?: (event: GestureResponderEvent) => void;
  href?: string;
  disabled?: boolean;
};

/** Alias kept for backward-compat with components that imported the old name. */
export type OptionalInteractiveProps = InteractiveProps;

type UseInteractiveElementParams = InteractiveProps;

/** Props to spread onto an interactive element. Empty when static. */
type InteractiveElementProps = {
  onPress?: (event: GestureResponderEvent) => void;
  onKeyDown?: (event: KeyboardEvent<HTMLElement>) => void;
  tabIndex?: number;
  href?: string;
  "aria-disabled"?: boolean;
};

type UseInteractiveElementResult = {
  /** Drives the boolean `interactive` variant on styled components. */
  isInteractive: boolean;
  /** Tag for tamagui's `render` prop. Disabled links render as `span` since `<a>` without `href` confuses screen readers. Undefined when static. */
  renderTag: "a" | "button" | "span" | undefined;
  /** Props to spread onto the rendered element. */
  interactiveProps: InteractiveElementProps;
};

/**
 * Shared interactive wiring for Card, ListItem, etc. — merges `onPress` with
 * solito navigation, gates on `disabled`, handles Enter/Space activation.
 *
 * `kind` is the source of truth when provided; otherwise it's inferred from
 * the presence of `href` / `onPress` (href wins → link with `onPress` as
 * analytics side-effect, handler-only → button, neither → static).
 * Components that need the inference suppressed (e.g. a button with no
 * handler yet) should pass `kind` explicitly.
 */
export const useInteractiveElement = ({
  kind,
  onPress,
  href,
  replace,
  scroll,
  shallow,
  disabled,
}: UseInteractiveElementParams): UseInteractiveElementResult => {
  // `useLink` must always be called (hook rules). When there's no href we
  // pass a placeholder and ignore the result downstream.
  const linkResult = useLink({
    href: href ?? "/",
    replace,
    scroll,
    shallow,
  });

  const resolvedKind: InteractiveKind =
    kind ??
    (href !== undefined ? "link" : onPress !== undefined ? "button" : "static");
  const isInteractive = resolvedKind !== "static";

  const resolvedOnPress = useCallback(
    (event: GestureResponderEvent) => {
      if (disabled) {
        return;
      }
      onPress?.(event);
      if (resolvedKind === "link") {
        linkResult.onPress?.(event);
      }
    },
    [disabled, resolvedKind, onPress, linkResult.onPress],
  );

  const onKeyDown = useCallback(
    (event: KeyboardEvent<HTMLElement>) => {
      // Don't react to events bubbling from interactive children (form fields,
      // nested buttons). Only act on events targeting this element itself.
      if (event.target !== event.currentTarget) {
        return;
      }
      if (disabled) {
        return;
      }
      if (event.key === " " || event.key === "Enter") {
        event.preventDefault();
        // Trigger native click — flows through React's onClick (which RNW
        // wires from onPress) so we don't have to bridge KeyboardEvent →
        // GestureResponderEvent.
        event.currentTarget.click();
      }
    },
    [disabled],
  );

  const interactiveProps = useMemo<InteractiveElementProps>(() => {
    if (!isInteractive) {
      return {};
    }
    return {
      onPress: resolvedOnPress,
      onKeyDown,
      tabIndex: disabled ? -1 : 0,
      ...(resolvedKind === "link" && isWeb && !disabled ? { href } : {}),
      ...(disabled ? { "aria-disabled": true } : {}),
    };
  }, [isInteractive, resolvedKind, resolvedOnPress, onKeyDown, disabled, href]);

  const renderTag: UseInteractiveElementResult["renderTag"] =
    resolvedKind === "link"
      ? disabled
        ? "span"
        : "a"
      : resolvedKind === "button"
        ? "button"
        : undefined;

  return { isInteractive, renderTag, interactiveProps };
};

export type { InteractiveElementProps, UseInteractiveElementParams };
